import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import React, { useState } from 'react';
import { DispatchParamList } from '../../../typings/Params';
import { useAuth } from '../../../context/Auth';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { useQuery } from '@tanstack/react-query';
import { dispatchService } from '../../../services/central-api/dispatch';
import Toast from 'react-native-toast-message';
import { translate } from '../../../services/translate';
import { UnitNameWithCategory, ClientError } from '../../../services/central-api/base-service';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import CustomButton from '../../../components/CustomButton';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import FilterInput from '../../../components/Filter/components/FilterInput';
import DataTableContainer from '../../../components/DataTableContainer';
import MyDataTable from '../../../components/MyDataTable';
import DropdownButton from '../../../components/DropdownButton';
import { Pagination } from '../../../components/Pagination';
import DeleteModal from '../../../components/DeleteModal';
import FilterCheckBox from '../../../components/Filter/components/FilterCheckBox';
import Filter from './Filters';

export default function UnitCategories() {
    const route = useRoute<RouteProp<DispatchParamList, 'UnitCategories'>>();
    const navigation = useNavigation<NavigationProp<DispatchParamList>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<DispatchParamList['UnitCategories']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [selectedUnitCategory, setSelectedUnitCategory] = useState<UnitCategory>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getPaginatedUnitCategories', filters],
        queryFn: () => dispatchService.getPaginatedUnitCategories(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;
    const { data, refetch } = queryResult;

    function showDeleteConfirmationDialog(unitCategory: UnitCategory) {
        setSelectedUnitCategory(unitCategory);
        setDeleteModalVisible(true);
    }

    async function confirmDelete(unitCategory: UnitCategory) {
        try {
            await dispatchService.deleteUnitCategory(unitCategory.id);
            Toast.show({ type: 'sentinelxSuccess', text1: translate('ActionSuccessfully') });
            setDeleteModalVisible(false);
            refetch();
        } catch (err) {
            if (err instanceof UnitNameWithCategory) {
                Toast.show({ type: 'sentinelxError', text1: translate('UnitNameWithCategory') });
            } else if (err instanceof ClientError) {
                Toast.show({ type: 'sentinelxError', text1: translate(err.message) });
            } else {
                console.error(err);
                Toast.show({ type: 'sentinelxError', text1: translate('unexpectedError') });
            }
        }
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));
        }
    }

    return (
        <Container>
            <Header
                title={translate('unitCategory')}
                actions={
                    <CustomButton
                        text={translate('register')}
                        onPress={() => {
                            setSelectedUnitCategory(undefined);
                            navigation.navigate('UnitCategory');
                        }}
                    />
                }
            />
            <Card>
                <DataTableHeader
                    zIndex={2}
                    title={translate('registeredUnitCategories')}
                    filter={
                        (authenticatedUser?.isAdmin || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_category) && <Filter
                            filters={filters}
                            setFilters={setFilters}
                        />
                    }
                    filterInput={
                        <FilterInput
                            value={textFilter}
                            placeholder={translate('typeToSearch')}
                            onChange={setTextFilter}
                            onKeyPress={handleKeyDown}
                        />
                    }
                />

                <DataTableContainer
                    dataTable={
                        <MyDataTable
                            columns={[
                                {
                                    name: 'ID',
                                    selector: row => row.id,
                                    grow: 0
                                },
                                {
                                    name: translate('name'),
                                    selector: row => row.name,
                                    wrap: true,
                                    grow: 2
                                },
                                {
                                    name: translate('type'),
                                    selector: row => translate(row.type),
                                    wrap: true,
                                },
                                {
                                    name: translate('actingBody'),
                                    selector: row => row.actingBody?.name ?? '',
                                    wrap: true,
                                },
                                {
                                    name: translate('configuration'),
                                    cell: row => (
                                        <View style={{ flexDirection: 'column', gap: 8, paddingVertical: 12 }}>
                                            <FilterCheckBox
                                                label={translate('hasPlate')}
                                                onChange={() => { }}
                                                disabled
                                                value={row.hasPlate}
                                                labelStyle={{ fontSize: 14 }}
                                            />

                                            <FilterCheckBox
                                                label={translate('generateOSE')}
                                                onChange={() => { }}
                                                disabled
                                                value={row.generateOSE}
                                                labelStyle={{ fontSize: 14 }}
                                            />
                                        </View>
                                    ),
                                    wrap: true,
                                },
                                {
                                    button: true,
                                    cell: row => (
                                        <DropdownButton
                                            actions={[
                                                {
                                                    content: translate('editInformation'),
                                                    onPress: () => navigation.navigate('UnitCategory', { id: row.id })
                                                },
                                                {
                                                    content: translate('delete'),
                                                    variant: 'danger',
                                                    onPress: () => showDeleteConfirmationDialog(row),
                                                }
                                            ]}
                                        />
                                    )
                                }
                            ]}
                            data={data?.rows ?? []}
                            progressPending={isLoading}
                        />
                    }
                    pagination={
                        <Pagination
                            totalItems={data?.count ?? 0}
                            currentPage={filters.page}
                            pageSize={filters.limit}
                            setPageSize={pageSize => {
                                setFilters(old => ({
                                    ...old,
                                    limit: pageSize,
                                    page: 0,
                                }));

                                navigation.setParams({
                                    ...route.params,
                                    limit: pageSize,
                                    page: 1,
                                });
                            }}
                            setPage={page => {
                                setFilters(old => ({
                                    ...old,
                                    page
                                }));

                                navigation.setParams({
                                    ...route.params,
                                    page: page + 1
                                });
                            }}
                        />
                    }
                />
            </Card>

            {selectedUnitCategory && (
                <DeleteModal
                    setModalVisible={setDeleteModalVisible}
                    isModalVisible={isDeleteModalVisible}
                    itemName={selectedUnitCategory.name}
                    onSubmit={async () => {
                        await confirmDelete(selectedUnitCategory);
                    }}
                />
            )}
        </Container>
    );
}
